import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { AppNavbar } from "@/components/app-navbar";
import { NfpCountdown } from "@/components/NfpCountdown";
import { bumpLastSeen } from "@/lib/admin-overview.functions";
import { getOnboardingStatus } from "@/lib/user.functions";

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    if (typeof window === "undefined") return { user: null };
    const { supabase } = await import("@/integrations/supabase/client");
    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session?.user) throw redirect({ to: "/login" });
    const user = data.session.user;
    const isAdminEmail = (user.email ?? "").toLowerCase() === "ahmity@gmail.com";
    if (!isAdminEmail) {
      // Cache the "no onboarding needed" decision for 30 min, then re-check
      // so disclaimer/risk version bumps force users back through /onboarding.
      const completedKey = `forexmind:onboarded:${user.id}`;
      const checkedKey = `forexmind:onboarded:checked-at:${user.id}`;
      const FRESH_MS = 30 * 60_000;
      const cached = localStorage.getItem(completedKey) === "true";
      const checkedAt = Number(localStorage.getItem(checkedKey) ?? 0);
      if (cached && Date.now() - checkedAt < FRESH_MS) return { user };

      const status = await getOnboardingStatus();
      if (status.needsOnboarding) {
        // Important: clear the stale flag so we don't loop back here next time.
        localStorage.removeItem(completedKey);
        localStorage.removeItem(checkedKey);
        throw redirect({ to: "/onboarding" });
      }
      localStorage.setItem(completedKey, "true");
      localStorage.setItem(checkedKey, String(Date.now()));
    }
    return { user };
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const { user } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Bump last_seen_at once per session so the admin overview can show "active today" + churn risk.
  useEffect(() => {
    if (!user) return;
    const key = `lovable:lastSeenBumpedAt:${user.id}`;
    const last = Number(localStorage.getItem(key) ?? 0);
    if (Date.now() - last < 15 * 60_000) return;
    localStorage.setItem(key, String(Date.now()));
    void bumpLastSeen().catch(() => {});
  }, [user]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <AppNavbar email={user?.email} />
      <NfpCountdown />
      <div
        key={pathname}
        className="flex min-w-0 flex-1 overflow-x-hidden animate-in fade-in slide-in-from-bottom-1 duration-300"
      >
        <Outlet />
      </div>
    </div>
  );
}
